import { useRef, useEffect, useCallback } from "react";
import { alarmRecords } from "@/mocks/alarm";

const hourlyAlarms = [0, 0, 1, 0, 0, 1, 2, 3, 2, 1, 1, 0, 1, 0, 1, 2, 1, 0, 1, 2, 1, 0, 0, 0];
const weekTrend = [5, 7, 4, 6, 8, 5, 8];
const weekLabels = ["周一", "周二", "周三", "周四", "周五", "周六", "今日"];

export default function AlarmStats() {
  const hourlyRef = useRef<HTMLCanvasElement>(null);
  const weekRef = useRef<HTMLCanvasElement>(null);

  const total = alarmRecords.length;
  const urgent = alarmRecords.filter(a => a.level === "紧急").length;
  const unhandled = alarmRecords.filter(a => a.status === "未处理").length;
  const handling = alarmRecords.filter(a => a.status === "处理中").length;
  const handled = alarmRecords.filter(a => a.status === "已处理").length;
  const handleRate = Math.round((handled / total) * 100);

  const drawHourly = useCallback(() => {
    const canvas = hourlyRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const maxV = Math.max(...hourlyAlarms, 1);
    const barW = W / hourlyAlarms.length;
    hourlyAlarms.forEach((v, i) => {
      if (v === 0) return;
      const barH = (v / maxV) * (H - 8);
      const x = i * barW + barW * 0.15;
      const bw = barW * 0.7;
      const y = H - barH;
      const isHigh = v >= 2;
      ctx.fillStyle = isHigh ? "#ef4444" : "#f97316";
      ctx.beginPath();
      ctx.roundRect(x, y, bw, barH, 2);
      ctx.fill();
    });
    // Current hour marker
    const now = new Date().getHours();
    const cx = now * barW + barW / 2;
    ctx.beginPath();
    ctx.moveTo(cx, 0);
    ctx.lineTo(cx, H);
    ctx.strokeStyle = "rgba(232,83,26,0.3)";
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.stroke();
    ctx.setLineDash([]);
  }, []);

  const drawWeek = useCallback(() => {
    const canvas = weekRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const maxV = Math.max(...weekTrend) + 2;
    const padL = 4, padR = 4, padT = 4, padB = 14;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;

    // Area
    const grad = ctx.createLinearGradient(0, padT, 0, padT + chartH);
    grad.addColorStop(0, "rgba(232,83,26,0.2)");
    grad.addColorStop(1, "rgba(232,83,26,0)");
    ctx.beginPath();
    weekTrend.forEach((v, i) => {
      const x = padL + (i / (weekTrend.length - 1)) * chartW;
      const y = padT + chartH - ((v / maxV) * chartH);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.lineTo(padL + chartW, padT + chartH);
    ctx.lineTo(padL, padT + chartH);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Line
    ctx.beginPath();
    weekTrend.forEach((v, i) => {
      const x = padL + (i / (weekTrend.length - 1)) * chartW;
      const y = padT + chartH - ((v / maxV) * chartH);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#E8531A";
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.stroke();

    // Dots
    weekTrend.forEach((v, i) => {
      const x = padL + (i / (weekTrend.length - 1)) * chartW;
      const y = padT + chartH - ((v / maxV) * chartH);
      ctx.beginPath();
      ctx.arc(x, y, i === weekTrend.length - 1 ? 3.5 : 2, 0, Math.PI * 2);
      ctx.fillStyle = i === weekTrend.length - 1 ? "#E8531A" : "rgba(232,83,26,0.6)";
      ctx.fill();
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(weekLabels[i], x, H - 2);
    });
  }, []);

  useEffect(() => {
    drawHourly();
    drawWeek();
  }, [drawHourly, drawWeek]);

  return (
    <div className="grid grid-cols-7 gap-3">
      {/* KPI cards */}
      {[
        { label: "今日告警总数", value: total, icon: "ri-alarm-warning-line", color: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200", sub: "较昨日 +3" },
        { label: "紧急告警", value: urgent, icon: "ri-error-warning-line", color: "text-red-600", bg: "bg-red-50", border: "border-red-200", sub: "需立即处置" },
        { label: "待处理", value: unhandled, icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", sub: "超时风险" },
        { label: "处理中", value: handling, icon: "ri-loader-4-line", color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", sub: "进行中" },
        { label: "已处理", value: handled, icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", sub: `处置率 ${handleRate}%` },
      ].map(s => (
        <div key={s.label} className={`col-span-1 ${s.bg} border ${s.border} rounded-xl p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white`}>
              <i className={`${s.icon} text-lg ${s.color}`}></i>
            </div>
          </div>
          <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          <p className="text-[10px] text-gray-300 mt-0.5">{s.sub}</p>
        </div>
      ))}

      {/* Hourly chart */}
      <div className="col-span-1 bg-white border border-gray-100 rounded-xl p-3">
        <p className="text-xs text-gray-500 mb-1 font-medium">分时告警分布</p>
        <canvas ref={hourlyRef} width={160} height={52} className="w-full" />
        <p className="text-[10px] text-gray-300 mt-1">0时 → 23时</p>
      </div>

      {/* Weekly trend */}
      <div className="col-span-1 bg-white border border-gray-100 rounded-xl p-3">
        <p className="text-xs text-gray-500 mb-1 font-medium">近7日趋势</p>
        <canvas ref={weekRef} width={160} height={52} className="w-full" />
        <p className="text-[10px] text-gray-300 mt-1">本周告警走势</p>
      </div>
    </div>
  );
}
